import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { buildUrl, createRequest } from './common/httpUtils'
import { requestFinished, requestStart } from './UI'
import { retrieveResponseErrorMessage } from '../utils/errorUtils'
import type { RootState } from '../store'

interface FetchEventTriggersArg {
  groupId?: GroupId | number | null
}

interface FetchEventTriggersResult {
  groupId?: GroupId | number | null
  deviceEventTriggers: IDeviceEventTrigger[]
  mamoriosEventTriggers: IMamoriosEventTrigger[]
  stayNotifications: IStayNotification[]
  triggers: IEventTrigger[]
}

const ensureNumber = (value: unknown): number => (
  typeof value === 'number' ? value : Number(value)
)

const ensureNumberArray = (value: unknown): number[] => (
  Array.isArray(value) ? value.map((item) => ensureNumber(item)) : []
)

const ensureMamorioIdentifierArray = (value: unknown): (string | number)[] => (
  Array.isArray(value) ? value.map((item) => (typeof item === 'number' ? item : String(item))) : []
)

const ensureUsers = (value: unknown): IEventTriggerUser[] => (
  Array.isArray(value)
    ? value.map((item) => ({
      id: ensureNumber((item as IEventTriggerUser).id),
      email: (item as IEventTriggerUser).email,
    })).filter((user) => Boolean(user.email))
    : []
)

const toNullableNumber = (value: unknown): number | null => {
  if (value === undefined || value === null) return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

const compactObject = <T extends Record<string, any>>(obj: T): T => {
  const entries = Object.entries(obj).filter(([, value]) => value !== undefined)
  return Object.fromEntries(entries) as T
}

const ensureEventTriggerMamorios = (value: unknown): IEventTriggerMamorio[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((item) => {
      const raw = item as Partial<IEventTriggerMamorio> & { id?: unknown; major?: unknown; minor?: unknown }
      const id = ensureNumber(raw.id)

      if (Number.isNaN(id)) {
        return null
      }

      return {
        id,
        name: typeof raw.name === 'string' ? raw.name : '',
        major: toNullableNumber(raw.major),
        minor: toNullableNumber(raw.minor),
      }
    })
    .filter((mamorio): mamorio is IEventTriggerMamorio => mamorio !== null)
}

const toDeviceEventTrigger = (trigger: any): IDeviceEventTrigger => ({
  kind: 'device',
  id: ensureNumber(trigger.id),
  device_id: trigger.device_id ?? null,
  name: trigger.name ?? '',
  mail_subject: trigger.mail_subject ?? null,
  mail_body: trigger.mail_body ?? null,
  cooldown_min: toNullableNumber(trigger.cooldown_min),
  active: Boolean(trigger.active),
  phone_notification: Boolean(trigger.phone_notification),
  phone_text: trigger.phone_text ?? null,
  group_id: ensureNumber(trigger.group_id),
  user_ids: ensureNumberArray(trigger.user_ids),
  users: ensureUsers(trigger.users),
  emails: Array.isArray(trigger.emails) ? trigger.emails : undefined,
  slack_webhook_url: trigger.slack_webhook_url ?? null,
  stay_mode_enabled: typeof trigger.stay_mode_enabled === 'boolean' ? trigger.stay_mode_enabled : undefined,
  stay_max_gap_seconds: toNullableNumber(trigger.stay_max_gap_seconds),
  mamorio_ids: ensureMamorioIdentifierArray(trigger.mamorio_ids),
  mamorios: ensureEventTriggerMamorios(trigger.mamorios),
})

const toMamoriosEventTrigger = (trigger: any): IMamoriosEventTrigger => {
  const skipAllLost = typeof trigger.skip_all_lost_notification === 'boolean'
    ? trigger.skip_all_lost_notification
    : undefined

  return {
    kind: 'mamorios',
    id: ensureNumber(trigger.id),
    name: trigger.name ?? '',
    mail_subject: trigger.mail_subject ?? null,
    mail_body: trigger.mail_body ?? null,
    cooldown_min: toNullableNumber(trigger.cooldown_min),
    active: Boolean(trigger.active),
    phone_notification: Boolean(trigger.phone_notification),
    phone_text: trigger.phone_text ?? null,
    group_id: ensureNumber(trigger.group_id),
    user_ids: ensureNumberArray(trigger.user_ids),
    users: ensureUsers(trigger.users),
    emails: Array.isArray(trigger.emails) ? trigger.emails : undefined,
    threshold_minutes: toNullableNumber(trigger.threshold_minutes),
    skip_all_lost_notification: skipAllLost,
    mamorio_ids: ensureMamorioIdentifierArray(trigger.mamorio_ids),
    device_ids: ensureNumberArray(trigger.device_ids),
    exclude_times_attributes: trigger.exclude_times_attributes,
    slack_webhook_url: trigger.slack_webhook_url ?? null,
    mamorios: ensureEventTriggerMamorios(trigger.mamorios),
  }
}

const toStayNotification = (notification: any): IStayNotification => ({
  kind: 'stay',
  id: ensureNumber(notification.id),
  name: notification.name ?? '',
  mail_subject: notification.mail_subject ?? null,
  mail_body: notification.mail_body ?? null,
  cooldown_min: toNullableNumber(notification.cooldown_min),
  active: Boolean(notification.active),
  phone_notification: Boolean(notification.phone_notification),
  phone_text: notification.phone_text ?? null,
  group_id: ensureNumber(notification.group_id),
  user_ids: ensureNumberArray(notification.user_ids),
  users: ensureUsers(notification.users),
  emails: Array.isArray(notification.emails) ? notification.emails : undefined,
  slack_webhook_url: notification.slack_webhook_url ?? null,
  threshold_minutes: toNullableNumber(notification.threshold_minutes),
  max_gap_seconds: toNullableNumber(notification.max_gap_seconds),
  mamorio_ids: ensureMamorioIdentifierArray(notification.mamorio_ids),
  device_ids: ensureNumberArray(notification.device_ids),
  mamorios: ensureEventTriggerMamorios(notification.mamorios),
})

export const fetchEventTriggers = createAsyncThunk<
FetchEventTriggersResult,
FetchEventTriggersArg | undefined,
{
  state: RootState
  rejectValue: string
}
>(
  'eventTriggers/fetchEventTriggers',
  async (arg, thunkApi) => {
    const { dispatch, rejectWithValue, getState } = thunkApi
    const state = getState()
    const teamId = state.team.id
    if (!teamId) {
      return rejectWithValue('チーム情報を取得できませんでした。')
    }
    const groupId = arg?.groupId ?? null
    const params = new URLSearchParams()
    params.append('team_id', String(teamId))
    if (groupId) {
      params.append('group_id', String(groupId))
    }
    const queryString = params.toString()
    const query = queryString.length > 0 ? `?${queryString}` : ''

    dispatch(requestStart())

    try {
      const [deviceResponse, mamoriosResponse, stayResponse] = await Promise.all([
        fetch(createRequest(buildUrl(`/api/v1/device_event_triggers${query}`), 'GET')),
        fetch(createRequest(buildUrl(`/api/v1/mamorios_event_triggers${query}`), 'GET')),
        fetch(createRequest(buildUrl(`/api/v1/stay_notifications${query}`), 'GET')),
      ])

      const parseResponse = async (response: Response) => {
        const data = await response.json()
        if (!response.ok) {
          const errorMessage = (data && data.error) ? data.error : response.statusText
          throw new Error(errorMessage)
        }
        return data
      }

      const deviceData = await parseResponse(deviceResponse)
      const mamoriosData = await parseResponse(mamoriosResponse)
      const stayData = await parseResponse(stayResponse)

      const rawDeviceTriggers = Array.isArray(deviceData)
        ? deviceData
        : deviceData?.device_event_triggers
      const rawMamoriosTriggers = Array.isArray(mamoriosData)
        ? mamoriosData
        : mamoriosData?.mamorios_event_triggers
      const rawStayNotifications = Array.isArray(stayData)
        ? stayData
        : stayData?.stay_notifications

      const deviceEventTriggers = Array.isArray(rawDeviceTriggers)
        ? rawDeviceTriggers.map((item) => toDeviceEventTrigger(item))
        : []

      const mamoriosEventTriggers = Array.isArray(rawMamoriosTriggers)
        ? rawMamoriosTriggers.map((item) => toMamoriosEventTrigger(item))
        : []

      const stayNotifications = Array.isArray(rawStayNotifications)
        ? rawStayNotifications.map((item) => toStayNotification(item))
        : []

      return {
        groupId,
        deviceEventTriggers,
        mamoriosEventTriggers,
        stayNotifications,
        triggers: [...deviceEventTriggers, ...mamoriosEventTriggers, ...stayNotifications],
      }
    } catch (error: unknown) {
      return rejectWithValue(retrieveResponseErrorMessage(error))
    } finally {
      dispatch(requestFinished())
    }
  },
)

type DeviceTriggerUpdateParams = {
  name?: string
  mail_subject?: string | null
  mail_body?: string | null
  cooldown_min?: number | null
  active?: boolean
  phone_notification?: boolean
  phone_text?: string | null
  emails?: string[]
  device_id?: number | null
  group_id: number | GroupId
  slack_webhook_url?: string | null
  stay_mode_enabled?: boolean
  stay_max_gap_seconds?: number | null
  mamorio_ids?: (string | number)[]
}

type MamoriosTriggerUpdateParams = DeviceTriggerUpdateParams & {
  threshold_minutes?: number | null
  skip_all_lost_notification?: boolean
  mamorio_ids?: (string | number)[]
  device_ids?: number[]
  slack_webhook_url?: string | null
}

type StayNotificationUpdateParams = {
  group_id: number | GroupId
  name?: string
  mail_subject?: string | null
  mail_body?: string | null
  cooldown_min?: number | null
  active?: boolean
  phone_notification?: boolean
  phone_text?: string | null
  slack_webhook_url?: string | null
  threshold_minutes?: number | null
  max_gap_seconds?: number | null
  mamorio_ids?: (string | number)[]
  device_ids?: number[]
  emails?: string[]
  user_ids?: number[]
}

export type UpdateEventTriggerArg =
  | { kind: 'device'; id: number; params: DeviceTriggerUpdateParams }
  | { kind: 'mamorios'; id: number; params: MamoriosTriggerUpdateParams }
  | { kind: 'stay'; id: number; params: StayNotificationUpdateParams }

export type DeleteEventTriggerArg = {
  kind: EventTriggerKind
  id: number
}

const buildDeviceEventTriggerPayload = (params: DeviceTriggerUpdateParams) => ({
  device_event_trigger: compactObject({
    name: params.name,
    mail_subject: params.mail_subject,
    mail_body: params.mail_body,
    cooldown_min: params.cooldown_min,
    active: params.active,
    phone_notification: params.phone_notification,
    phone_text: params.phone_text,
    emails: params.emails,
    device_id: params.device_id,
    group_id: ensureNumber(params.group_id),
    slack_webhook_url: params.slack_webhook_url,
    stay_mode_enabled: params.stay_mode_enabled,
    stay_max_gap_seconds: params.stay_max_gap_seconds,
    mamorio_ids: params.mamorio_ids,
  }),
})

const buildMamoriosEventTriggerPayload = (params: MamoriosTriggerUpdateParams) => ({
  mamorios_event_trigger: compactObject({
    name: params.name,
    mail_subject: params.mail_subject,
    mail_body: params.mail_body,
    cooldown_min: params.cooldown_min,
    active: params.active,
    phone_notification: params.phone_notification,
    phone_text: params.phone_text,
    emails: params.emails,
    threshold_minutes: params.threshold_minutes,
    skip_all_lost_notification: params.skip_all_lost_notification,
    mamorio_ids: params.mamorio_ids,
    device_ids: params.device_ids,
    group_id: ensureNumber(params.group_id),
    slack_webhook_url: params.slack_webhook_url,
  }),
})

const buildStayNotificationPayload = (params: StayNotificationUpdateParams) => ({
  stay_notification: compactObject({
    name: params.name,
    mail_subject: params.mail_subject,
    mail_body: params.mail_body,
    cooldown_min: params.cooldown_min,
    active: params.active,
    phone_notification: params.phone_notification,
    phone_text: params.phone_text,
    slack_webhook_url: params.slack_webhook_url,
    threshold_minutes: params.threshold_minutes,
    max_gap_seconds: params.max_gap_seconds,
    mamorio_ids: params.mamorio_ids,
    device_ids: params.device_ids,
    group_id: ensureNumber(params.group_id),
    emails: params.emails,
    user_ids: params.user_ids,
  }),
})

const extractDeviceTriggerFromResponse = (data: any): any => (
  data?.device_event_trigger ?? data
)

const extractMamoriosTriggerFromResponse = (data: any): any => (
  data?.mamorios_event_trigger ?? data
)

const extractStayNotificationFromResponse = (data: any): any => (
  data?.stay_notification ?? data
)

export const updateEventTrigger = createAsyncThunk<IEventTrigger, UpdateEventTriggerArg, { rejectValue: string }>(
  'eventTriggers/updateEventTrigger',
  async (arg, thunkApi) => {
    const { dispatch, rejectWithValue } = thunkApi
    const { id, kind } = arg

    const url = (() => {
      switch (kind) {
        case 'device':
          return `/api/v1/device_event_triggers/${id}`
        case 'mamorios':
          return `/api/v1/mamorios_event_triggers/${id}`
        case 'stay':
          return `/api/v1/stay_notifications/${id}`
        default:
          return `/api/v1/device_event_triggers/${id}`
      }
    })()

    let body: any
    if (kind === 'device') {
      body = buildDeviceEventTriggerPayload(arg.params)
    } else if (kind === 'mamorios') {
      body = buildMamoriosEventTriggerPayload(arg.params)
    } else {
      body = buildStayNotificationPayload(arg.params)
    }

    dispatch(requestStart())

    try {
      const requestUrl = buildUrl(url)
      const response = await fetch(
        createRequest(
          requestUrl,
          'PATCH',
          true,
          'application/json',
          body,
        ),
      )

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        const errorMessages = Array.isArray(data?.error_messages) ? data.error_messages.join('\n') : null
        const message = errorMessages || data?.error_message || data?.error || response.statusText
        return rejectWithValue(message)
      }

      if (kind === 'device') {
        return toDeviceEventTrigger(extractDeviceTriggerFromResponse(data))
      }

      if (kind === 'mamorios') {
        return toMamoriosEventTrigger(extractMamoriosTriggerFromResponse(data))
      }

      return toStayNotification(extractStayNotificationFromResponse(data))
    } catch (error: unknown) {
      return rejectWithValue(retrieveResponseErrorMessage(error))
    } finally {
      dispatch(requestFinished())
    }
  },
)

export const deleteEventTrigger = createAsyncThunk<DeleteEventTriggerArg, DeleteEventTriggerArg, { rejectValue: string }>(
  'eventTriggers/deleteEventTrigger',
  async (arg, thunkApi) => {
    const { dispatch, rejectWithValue } = thunkApi
    const { id, kind } = arg

    const url = (() => {
      switch (kind) {
        case 'device':
          return `/api/v1/device_event_triggers/${id}`
        case 'mamorios':
          return `/api/v1/mamorios_event_triggers/${id}`
        case 'stay':
          return `/api/v1/stay_notifications/${id}`
        default:
          return `/api/v1/device_event_triggers/${id}`
      }
    })()

    dispatch(requestStart())

    try {
      const response = await fetch(
        createRequest(
          buildUrl(url),
          'DELETE',
        ),
      )

      if (!response.ok && response.status !== 204) {
        const data = await response.json().catch(() => ({}))
        const message = data?.error || response.statusText
        return rejectWithValue(message)
      }

      return arg
    } catch (error: unknown) {
      return rejectWithValue(retrieveResponseErrorMessage(error))
    } finally {
      dispatch(requestFinished())
    }
  },
)

interface EventTriggersState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  deviceEventTriggers: IDeviceEventTrigger[]
  mamoriosEventTriggers: IMamoriosEventTrigger[]
  stayNotifications: IStayNotification[]
  triggers: IEventTrigger[]
  lastFetchedGroupId: number | GroupId | null
}

const initialState: EventTriggersState = {
  status: 'idle',
  error: null,
  deviceEventTriggers: [],
  mamoriosEventTriggers: [],
  stayNotifications: [],
  triggers: [],
  lastFetchedGroupId: null,
}

const eventTriggersSlice = createSlice({
  name: 'eventTriggers',
  initialState,
  reducers: {
    resetEventTriggers: () => initialState,
    clearEventTriggerError: (state) => ({
      ...state,
      error: null,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventTriggers.pending, (state) => ({
        ...state,
        status: 'loading',
        error: null,
      }))
      .addCase(fetchEventTriggers.fulfilled, (state, action: PayloadAction<FetchEventTriggersResult>) => ({
        ...state,
        status: 'succeeded',
        error: null,
        deviceEventTriggers: action.payload.deviceEventTriggers,
        mamoriosEventTriggers: action.payload.mamoriosEventTriggers,
        stayNotifications: action.payload.stayNotifications,
        triggers: action.payload.triggers,
        lastFetchedGroupId: action.payload.groupId ?? null,
      }))
      .addCase(fetchEventTriggers.rejected, (state, action) => ({
        ...state,
        status: 'failed',
        error: action.payload || action.error.message || 'イベント通知の取得に失敗しました',
      }))
      .addCase(updateEventTrigger.pending, (state) => ({
        ...state,
        status: 'loading',
        error: null,
      }))
      .addCase(updateEventTrigger.fulfilled, (state, action: PayloadAction<IEventTrigger>) => {
        if (action.payload.kind === 'device') {
          const nextDevice = state.deviceEventTriggers.map((trigger) => (
            trigger.id === action.payload.id ? action.payload as IDeviceEventTrigger : trigger
          ))

          return {
            ...state,
            status: 'succeeded',
            deviceEventTriggers: nextDevice,
            triggers: [...nextDevice, ...state.mamoriosEventTriggers, ...state.stayNotifications],
          }
        }

        if (action.payload.kind === 'mamorios') {
          const nextMamorios = state.mamoriosEventTriggers.map((trigger) => (
            trigger.id === action.payload.id ? action.payload as IMamoriosEventTrigger : trigger
          ))

          return {
            ...state,
            status: 'succeeded',
            mamoriosEventTriggers: nextMamorios,
            triggers: [...state.deviceEventTriggers, ...nextMamorios, ...state.stayNotifications],
          }
        }

        const nextStay = state.stayNotifications.map((notification) => (
          notification.id === action.payload.id ? action.payload as IStayNotification : notification
        ))

        return {
          ...state,
          status: 'succeeded',
          stayNotifications: nextStay,
          triggers: [...state.deviceEventTriggers, ...state.mamoriosEventTriggers, ...nextStay],
        }
      })
      .addCase(updateEventTrigger.rejected, (state, action) => ({
        ...state,
        status: 'failed',
        error: action.payload || action.error.message || 'イベント通知の更新に失敗しました',
      }))
      .addCase(deleteEventTrigger.pending, (state) => ({
        ...state,
        status: 'loading',
        error: null,
      }))
      .addCase(deleteEventTrigger.fulfilled, (state, action: PayloadAction<DeleteEventTriggerArg>) => {
        if (action.payload.kind === 'device') {
          const nextDevice = state.deviceEventTriggers.filter((trigger) => trigger.id !== action.payload.id)
          return {
            ...state,
            status: 'succeeded',
            deviceEventTriggers: nextDevice,
            triggers: [...nextDevice, ...state.mamoriosEventTriggers, ...state.stayNotifications],
          }
        }

        if (action.payload.kind === 'mamorios') {
          const nextMamorios = state.mamoriosEventTriggers.filter((trigger) => trigger.id !== action.payload.id)

          return {
            ...state,
            status: 'succeeded',
            mamoriosEventTriggers: nextMamorios,
            triggers: [...state.deviceEventTriggers, ...nextMamorios, ...state.stayNotifications],
          }
        }

        const nextStay = state.stayNotifications.filter((notification) => notification.id !== action.payload.id)

        return {
          ...state,
          status: 'succeeded',
          stayNotifications: nextStay,
          triggers: [...state.deviceEventTriggers, ...state.mamoriosEventTriggers, ...nextStay],
        }
      })
      .addCase(deleteEventTrigger.rejected, (state, action) => ({
        ...state,
        status: 'failed',
        error: action.payload || action.error.message || 'イベント通知の削除に失敗しました',
      }))
  },
})

export const { resetEventTriggers, clearEventTriggerError } = eventTriggersSlice.actions

export default eventTriggersSlice.reducer
